// Top-up modal. All users currently checkout via Paddle (USD). Razorpay paths
// remain in the file behind the `paymentProvider` flag for future re-enablement.
import { useEffect, useMemo, useState } from "react";
import { Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { usePaywallTriggers } from "@/hooks/usePaywallTriggers";
import { supabase } from "@/integrations/supabase/client";
import { initializePaddle, createPaddleTransaction } from "@/lib/paddle";
import { formatCredits } from "@/lib/format-credits";
import { isNativeApp, isAndroidApp } from "@/lib/platform";
import { cn } from "@/lib/utils";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

export interface CreditPack {
  id: string;
  credits: number;
  amountInr: number;
  amountUsd: number; // dollars (e.g. 2.99)
  badge?: string;
  popular?: boolean;
}

export const CREDIT_PACKS: CreditPack[] = [
  { id: "mini",  credits: 5,   amountInr: 49,  amountUsd: 2.99,  badge: "✨ First Try" },
  { id: "small", credits: 15,  amountInr: 99,  amountUsd: 4.99 },
  { id: "hot",   credits: 22,  amountInr: 149, amountUsd: 6.99,  badge: "🔥 Hot" },
  { id: "value", credits: 38,  amountInr: 249, amountUsd: 9.99,  popular: true, badge: "⭐ Popular" },
  { id: "pro",   credits: 80,  amountInr: 499, amountUsd: 17.99, badge: "💎 Best Value" },
  { id: "mega",  credits: 160, amountInr: 999, amountUsd: 29.99, badge: "🚀 Pro" },
];

declare global {
  interface Window {
    Razorpay?: new (opts: Record<string, unknown>) => { open: () => void };
  }
}

const loadRazorpay = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

import { useRegion } from "@/hooks/useRegion";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** When true, shows the "out of credits" framing. */
  outOfCredits?: boolean;
  /** Pre-highlighted pack id (used by the emotional paywall). */
  defaultPackId?: string;
}

export const BuyCreditsDialog = ({ open, onOpenChange, outOfCredits, defaultPackId }: Props) => {
  const { user } = useAuth();
  const { refresh, credits } = useCredits();
  const { markPurchased } = usePaywallTriggers();
  const { toast } = useToast();
  const [busyPack, setBusyPack] = useState<string | null>(null);
  const region = useRegion();

  const orderedPacks = useMemo(() => {
    if (!defaultPackId) return CREDIT_PACKS;
    const preferred = CREDIT_PACKS.find((p) => p.id === defaultPackId);
    if (!preferred) return CREDIT_PACKS;
    return [preferred, ...CREDIT_PACKS.filter((p) => p.id !== defaultPackId)];
  }, [defaultPackId]);

  // Platform-aware billing:
  // - Native Android app    → Google Play Billing (Google policy: digital goods must use Play)
  // - Web, IST timezone     → Razorpay (INR) — gated to admins by edge fn until live keys land
  // - Web, everywhere else  → Paddle (USD)
  type Provider = "paddle" | "razorpay" | "play";
  const playBilling = isAndroidApp();
  const paymentProvider = (
    playBilling ? "play" : region === "in" ? "razorpay" : "paddle"
  ) as Provider;

  useEffect(() => {
    if (!open) return;
    if (paymentProvider === "razorpay") void loadRazorpay();
    else if (paymentProvider === "paddle") void initializePaddle().catch(() => { /* deferred until purchase */ });
    // Play Billing: no SDK preload needed here — handled by the native plugin
    // wired in Antigravity (Phase 2).
  }, [open, paymentProvider]);

  const buyRazorpay = async (pack: CreditPack) => {
    if (!user) return;
    const ok = await loadRazorpay();
    if (!ok || !window.Razorpay) throw new Error("Couldn't load Razorpay");

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) throw new Error("Please sign in again");

    const { data, error } = await supabase.functions.invoke("create-razorpay-order", {
      body: { packId: pack.id },
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (data?.error === "test_mode_restricted") {
      throw new Error(data.message ?? "Razorpay checkout is in test mode and temporarily restricted.");
    }
    if (error) throw error;
    if (!data?.orderId || !data?.keyId) throw new Error("Order setup failed");

    // Close our Dialog BEFORE opening Razorpay. Radix Dialog locks body scroll
    // and traps focus, which makes Razorpay's iframe laggy/unclickable on
    // mobile and desktop. We reopen on cancel so the user can pick again.
    onOpenChange(false);
    // Wait one frame so Radix can release scroll-lock + focus trap.
    await new Promise((r) => requestAnimationFrame(() => r(null)));

    await new Promise<void>((resolve, reject) => {
      const rzp = new window.Razorpay!({
        key: data.keyId,
        amount: data.amountPaise,
        currency: "INR",
        name: "FitAura AI",
        description: `${pack.credits} credits`,
        order_id: data.orderId,
        prefill: { email: user.email ?? "" },
        theme: { color: "#7c3aed" },
        handler: async (resp: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          try {
            const { data: ver, error: verErr } = await supabase.functions.invoke(
              "verify-razorpay-payment",
              { body: resp, headers: { Authorization: `Bearer ${accessToken}` } },
            );
            if (verErr) throw verErr;
            if (!ver?.success) throw new Error(ver?.error ?? "Verification failed");
            await refresh();
            await markPurchased(ver.creditsAdded ?? pack.credits);
            toast({ title: "Credits added 🎉", description: `+${ver.creditsAdded} credits. New balance: ${ver.newBalance}.` });
            resolve();
          } catch (e) { reject(e); }
        },
        modal: {
          ondismiss: () => {
            // Reopen our picker so the user can choose another pack.
            onOpenChange(true);
            reject(new Error("Payment cancelled"));
          },
          escape: true,
          backdropclose: false,
        },
      });
      rzp.open();
    });
  };

  const buyPaddle = async (pack: CreditPack) => {
    if (!user) return;
    await initializePaddle();
    const { transactionId } = await createPaddleTransaction(pack.id);

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      window.Paddle.Checkout.open({
        transactionId,
        customer: { email: user.email ?? "" },
        settings: {
          displayMode: "overlay",
          theme: "light",
          successUrl: `${window.location.origin}/checkout/success`,
          allowLogout: false,
          variant: "one-page",
        },
        eventCallback: async (ev: { name: string }) => {
          if (settled) return;
          if (ev.name === "checkout.completed") {
            settled = true;
            // Webhook is the source of truth; refresh a few times so the user sees the new balance.
            for (let i = 0; i < 6; i++) {
              await new Promise((r) => setTimeout(r, 1500));
              await refresh();
            }
            await markPurchased(pack.credits);
            toast({ title: "Payment received 🎉", description: `Crediting ${pack.credits} credits to your account…` });
            onOpenChange(false);
            resolve();
          } else if (ev.name === "checkout.closed") {
            settled = true;
            reject(new Error("Payment cancelled"));
          } else if (ev.name === "checkout.error") {
            settled = true;
            reject(new Error("Payment error"));
          }
        },
      });
    });
  };

  // Native Google Play Billing path. The actual purchase is handled by
  // src/lib/native-billing.ts which wraps the native plugin (added by
  // Antigravity in the native rebuild) and verifies via verify-play-purchase.
  const buyPlay = async (pack: CreditPack) => {
    const { buyPackViaPlay } = await import("@/lib/native-billing");
    const result = await buyPackViaPlay(pack);
    if (!result.success) {
      throw new Error(result.error ?? "Purchase failed");
    }
    await refresh();
    await markPurchased(result.creditsAdded ?? pack.credits);
    toast({
      title: "Credits added 🎉",
      description: `+${result.creditsAdded ?? pack.credits} credits. New balance: ${result.newBalance}.`,
    });
    onOpenChange(false);
  };

  const buy = async (pack: CreditPack) => {
    if (!user) return;
    setBusyPack(pack.id);
    try {
      if (paymentProvider === "razorpay") await buyRazorpay(pack);
      else if (paymentProvider === "play") await buyPlay(pack);
      else await buyPaddle(pack);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg !== "Payment cancelled") {
        toast({ title: "Purchase failed", description: msg, variant: "destructive" });
      }
    } finally {
      setBusyPack(null);
    }
  };



  // Per-pack display label (uppercase eyebrow shown above credit count).
  const packLabel = (id: string) => {
    switch (id) {
      case "mini":  return "Mini";
      case "small": return "Small";
      case "hot":   return "Hot";
      case "value": return "Value";
      case "pro":   return "Pro";
      case "mega":  return "Mega";
      default:      return id;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-h-[92vh] overflow-y-auto overscroll-contain p-0 sm:max-w-lg",
          "rounded-t-3xl sm:rounded-3xl",
          "border-white/10 bg-gradient-to-b from-card/95 to-background/95 backdrop-blur-2xl",
          "shadow-[0_30px_120px_-20px_hsl(var(--primary)/0.45)]",
        )}
      >
        {/* Sandbox/test-mode notice */}
        <PaymentTestModeBanner />

        <div className="px-5 pb-6 pt-5 sm:px-6 sm:pt-6">
          {/* Drag handle (mobile bottom-sheet feel) */}
          <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/15 sm:hidden" />

          <DialogHeader className="text-left">
            <DialogTitle asChild>
              <h2 className="font-display text-3xl font-bold text-foreground sm:text-4xl">
                {outOfCredits ? "Out of credits" : "Top up credits"}
              </h2>
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-muted-foreground">
              {outOfCredits
                ? "Buy more to keep generating outfits and try-ons."
                : `You currently have ${formatCredits(credits)} credits. Each generation uses 1 credit.`}
            </DialogDescription>
          </DialogHeader>

          {/* Pricing rows — horizontal layout */}
          <div className="mt-5 flex flex-col gap-3">
            {orderedPacks.map((pack) => {
              const busy = busyPack === pack.id;
              const isPreferred = defaultPackId === pack.id;
              const featured = pack.popular || isPreferred;
              // Show INR for native Android (Play Billing local currency) and
              // for IST-timezone web users who check out via Razorpay.
              const showInr = isAndroidApp() || region === "in";
              const priceLabel = showInr ? `₹${pack.amountInr}` : `$${pack.amountUsd.toFixed(2)}`;

              return (
                <div key={pack.id} className="relative pt-3">
                  {featured && (
                    <span className="absolute left-4 top-0 z-10 rounded-full bg-accent px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-accent-foreground shadow-[0_0_18px_-4px_hsl(var(--accent))]">
                      Hot · Best Value
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => buy(pack)}
                    disabled={!!busyPack}
                    className={cn(
                      "group relative flex w-full items-center gap-4 rounded-2xl border px-4 py-4 text-left transition-all",
                      featured
                        ? "border-accent/60 bg-card/60 shadow-[0_0_30px_-12px_hsl(var(--accent))]"
                        : "border-white/10 bg-card/40 hover:border-primary/30 hover:bg-card/60",
                      busyPack && busyPack !== pack.id && "opacity-50",
                    )}
                  >
                    {/* Left: label + credits */}
                    <div className="flex flex-1 flex-col">
                      <span className="font-display text-[11px] font-semibold uppercase tracking-[0.28em] text-muted-foreground">
                        {packLabel(pack.id)}
                      </span>
                      <span className="font-display mt-1 flex items-baseline gap-1.5 text-foreground">
                        <span className="text-3xl font-bold tabular-nums">{pack.credits}</span>
                        <span className="text-xs font-medium text-muted-foreground">Credits</span>
                      </span>
                    </div>

                    {/* Middle: price */}
                    <span className="font-display text-2xl font-bold tabular-nums text-foreground">
                      {priceLabel}
                    </span>

                    {/* Right: Buy pill */}
                    <span
                      className={cn(
                        "font-display ml-2 inline-flex h-10 min-w-[80px] items-center justify-center rounded-full px-5 text-sm font-bold text-primary-foreground transition-all",
                        "bg-gradient-to-r from-primary via-primary to-accent",
                        "shadow-[0_0_24px_-6px_hsl(var(--primary))] group-hover:shadow-[0_0_32px_-4px_hsl(var(--primary))]",
                      )}
                    >
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buy"}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Footer: secure checkout note + legal */}
          <div className="mt-6 flex flex-col items-center gap-2 border-t border-white/10 pt-4">
            <p className="flex items-center gap-1.5 text-center font-display text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/80">
              <Check className="h-3 w-3 text-success" />
              {paymentProvider === "play"
                ? "Secure checkout via Google Play."
                : "Secure checkout · cards, Apple Pay & Google Pay."}
            </p>
            <p className="flex items-center gap-3 text-[10px] text-muted-foreground/60">
              <a href="/privacy" className="uppercase tracking-[0.24em] hover:text-foreground">Privacy</a>
              <span className="opacity-40">·</span>
              <a href="/terms" className="uppercase tracking-[0.24em] hover:text-foreground">Terms of Service</a>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
